/**
 * SoleadLab 2.0 — Login Page
 *
 * Full-page login form. Redirects to the intended destination after login.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import type { AuthUser, OrgSummary } from '../stores/authStore';
import { apiFetch, ApiError } from '../services/apiFetch';
import { consumePendingInvite } from '../utils/pendingInvite';
import './LoginPage.css';

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Login returns 403 / FORBIDDEN with this message family when the account
 * exists but `email_verified=0`. We match on a substring so a future copy
 * tweak doesn't silently break the resend affordance.
 */
function isUnverifiedEmailError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    err.code === 'FORBIDDEN' &&
    err.message.toLowerCase().includes('verified')
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TD-7: when login fails with "email not verified", remember the address so
  // we can render an inline "Resend verification email" button. The user
  // can't navigate back to the post-register "Check your inbox" screen and
  // re-registering 409s — without this, unverified users are stuck.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const resendTimerRef = useRef<number | null>(null);

  const login = useAuthStore(s => s.login);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();

  // Cleanup resend cooldown timer on unmount.
  useEffect(() => {
    return () => {
      if (resendTimerRef.current !== null) {
        window.clearInterval(resendTimerRef.current);
      }
    };
  }, []);

  // If already logged in, redirect declaratively. Calling navigate() during
  // render is unreliable (the page may render `null` and the navigation
  // never fires, leaving a blank screen).
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setUnverifiedEmail(null);
    setResendStatus('idle');

    try {
      const data = await apiFetch<{
        token: string;
        user: AuthUser;
        currentOrg?: OrgSummary | null;
        orgs?: OrgSummary[];
      }>('/api/v2/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!data?.token) {
        throw new Error('No token in response');
      }

      login(
        data.token,
        data.user,
        data.currentOrg ?? null,
        data.orgs ?? []
      );

      // Resume any pending invitation (set during registration when the user
      // was sent here from /invite/:token). Takes priority over onboarding —
      // they accepted an invite, they don't need to create a new org.
      // consumePendingInvite reads + clears in one call.
      const pendingInvite = consumePendingInvite();
      if (pendingInvite) {
        navigate(`/invite/${pendingInvite}`, { replace: true });
        return;
      }

      // First-time users have no org memberships yet — push them through
      // onboarding before the editor. Returning users go to wherever they
      // were trying to reach (or /editor by default).
      const orgsList = data.orgs ?? [];
      // Onboarding takeover applies to either of the two valid landing targets
      // — /dashboard (new default) or /editor (pre-dashboard deep links).
      const isLanding = from === '/dashboard' || from === '/editor';
      const destination =
        orgsList.length === 0 && isLanding ? '/onboarding' : from;
      navigate(destination, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message :
        err instanceof Error ? err.message : 'Login failed';
      setError(message);
      if (isUnverifiedEmailError(err)) {
        setUnverifiedEmail(email.trim().toLowerCase());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail || resendCooldown > 0 || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await apiFetch('/api/v2/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email: unverifiedEmail }),
      });
      setResendStatus('sent');
      // Anti-spam: lock the button for 60s. Backend returns success even when
      // the account doesn't exist (anti-enumeration), so the cooldown also
      // discourages probing.
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      if (resendTimerRef.current !== null) {
        window.clearInterval(resendTimerRef.current);
      }
      resendTimerRef.current = window.setInterval(() => {
        setResendCooldown((c) => {
          if (c <= 1) {
            if (resendTimerRef.current !== null) {
              window.clearInterval(resendTimerRef.current);
              resendTimerRef.current = null;
            }
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__card">
        <div className="login-page__header">
          <h1 className="login-page__title">SoleadLab</h1>
          <p className="login-page__subtitle">Solar PV Inspection & Monitoring</p>
        </div>

        <form onSubmit={handleSubmit} className="login-page__form">
          <div className="login-page__field">
            <label className="login-page__label">Email</label>
            <input
              className="login-page__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="login-page__field">
            <label className="login-page__label">Password</label>
            <input
              className="login-page__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
            />
          </div>

          {error && <div className="login-page__error">{error}</div>}

          {unverifiedEmail && (
            <div className="login-page__resend">
              {resendStatus === 'sent' ? (
                <span className="login-page__resend-msg">
                  Verification email sent to <strong>{unverifiedEmail}</strong>. Check your inbox.
                </span>
              ) : (
                <>
                  <span className="login-page__resend-msg">
                    Didn't get the verification email?
                  </span>
                  <button
                    type="button"
                    className="login-page__resend-btn"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || resendStatus === 'sending'}
                  >
                    {resendStatus === 'sending'
                      ? 'Sending…'
                      : resendCooldown > 0
                        ? `Resend in ${resendCooldown}s`
                        : 'Resend verification email'}
                  </button>
                </>
              )}
              {resendStatus === 'error' && (
                <span className="login-page__resend-error">
                  Couldn't send right now — try again in a moment.
                </span>
              )}
            </div>
          )}

          <button
            type="submit"
            className="login-page__submit"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.875rem' }}>
          <span style={{ color: 'var(--sl-color-text-muted, #a1a1aa)' }}>
            Don't have an account?{' '}
          </span>
          <Link to="/register" style={{ color: 'var(--sl-color-primary, #3b82f6)' }}>
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}
